// Pure display logic for Reports → Kitchen flow (Stage 3).
import type { AvTReport, AvTRow } from './visibilityTypes'
import { FR } from './flowReportStrings'

/** "45 min", "2 h 5 min", "3 d 4 h" — or "—". */
export function formatMinutes(m: number | null | undefined): string {
  if (m == null || !Number.isFinite(m)) return '—'
  const mins = Math.max(0, Math.round(m))
  if (mins < 60) return `${mins} min`
  const h = Math.floor(mins / 60)
  if (h < 24) {
    const r = mins % 60
    return r ? `${h} h ${r} min` : `${h} h`
  }
  const d = Math.floor(h / 24)
  const rh = h % 24
  return rh ? `${d} d ${rh} h` : `${d} d`
}

/** A quantity with up to 3 decimals, no trailing zeros. */
export function fmtQty(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—'
  return String(Math.round(n * 1000) / 1000)
}

export function fmtPct(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—'
  return `${Math.round(n * 10) / 10}%`
}

/** Net of the small "other" movements, shown as one signed column. */
export function avtOtherNet(r: AvTRow): number {
  return Math.round((r.other_in_qty - r.other_out_qty - r.transfer_out_qty) * 1000) / 1000
}

/**
 * How a row's unexplained figure reads: positive is missing, negative is
 * extra (more on the shelf than the records explain). No figure without counts.
 */
export function avtVerdict(r: AvTRow): { kind: 'missing' | 'extra' | 'match' | 'none'; qty: number } {
  if (r.unexplained_qty == null) return { kind: 'none', qty: 0 }
  if (Math.abs(r.unexplained_qty) < 1e-6) return { kind: 'match', qty: 0 }
  return r.unexplained_qty > 0 ? { kind: 'missing', qty: r.unexplained_qty } : { kind: 'extra', qty: -r.unexplained_qty }
}

/** The rows for the "biggest unexplained" chart: counted rows, largest |value| first. */
export function avtChartRows(rows: AvTRow[], n = 8): { name: string; value: number }[] {
  return rows
    .filter((r) => r.unexplained_value != null && Math.abs(r.unexplained_value) > 0.005)
    .sort((a, b) => Math.abs(b.unexplained_value!) - Math.abs(a.unexplained_value!))
    .slice(0, n)
    .map((r) => ({ name: r.item_name, value: r.unexplained_value! }))
}

/** Which report sections a role may open (mirrors the route groups). The
 *  manager also reads stock counts here (Batch 6) — read only, and the only
 *  screen of counts a manager has (no Store Inventory). */
export function flowReportSections(role: string | null): string[] {
  if (role === 'admin' || role === 'accountant') return ['avt', 'requests', 'transit', 'cooking', 'stock', 'buying']
  if (role === 'manager') return ['requests', 'cooking', 'counts']
  return []
}

/** The counted row with the most stock missing (by value), or null. */
export function avtBiggestMissing(rows: AvTRow[]): AvTRow | null {
  let best: AvTRow | null = null
  for (const r of rows) {
    if (r.unexplained_value == null || r.unexplained_value <= 0.005) continue
    if (!best || r.unexplained_value > best.unexplained_value!) best = r
  }
  return best
}

/** "18 Oct" — a count's day, as staff say it. */
export function shortDay(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

/**
 * The kitchen check in one plain sentence, before any table. `tone` follows
 * what is MISSING only (extra stock is a miscount, not a loss).
 */
export function avtHeadline(r: AvTReport, money: (n: number) => string): { tone: 'bad' | 'ok' | 'none'; text: string } {
  if (r.needs_first_count) return { tone: 'none', text: FR.avt.firstCount }
  if (r.items_with_counts === 0 || !r.opening_count_at || !r.closing_count_at) return { tone: 'none', text: FR.avt.countAgain }
  const from = shortDay(r.opening_count_at)
  const to = shortDay(r.closing_count_at)
  if (r.missing_value > 0.005) {
    const big = avtBiggestMissing(r.rows)
    const biggest = big ? FR.avt.biggest(big.item_name, `${fmtQty(big.unexplained_qty)} ${big.unit}`, money(big.unexplained_value!)) : ''
    return { tone: 'bad', text: FR.avt.headlineMissing(money(r.missing_value), from, to) + biggest }
  }
  return { tone: 'ok', text: FR.avt.headlineClean(from, to) }
}
